import { useEffect, useState } from "react";

import { useToast } from "@/hooks/use-toast";

import { useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "@/pages/auth/auth.context";
import { BonusForm, BonusFormValues } from "./BonusForm";
import { BonusType } from "@/types/bonus";
import { format, parse } from "date-fns";
import { BackLink } from "@/components/brand/BackLink";

export default function EditBonus() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<BonusFormValues | null>(
    null,
  );

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axiosInstance
      .get(`/bonus/${id}`)
      .then((res) => {
        const bonus = res.data;
        setInitialValues({
          title: bonus.title,
          bonusType: bonus.bonusType as BonusType,
          startDate: parse(bonus.startDate, "yyyy-MM-dd", new Date()),
          endDate: parse(bonus.endDate, "yyyy-MM-dd", new Date()),
          requiredParties: bonus.requiredParties ?? null,
          dollarAmount:
            bonus.dollarAmount != null ? Number(bonus.dollarAmount) : null,
          topN: bonus.topN ?? null,
          notes: bonus.notes ?? null,
        });
      })
      .catch(() => {
        setError("Failed to load bonus program");
        toast({
          title: "Error",
          description: "Failed to load bonus program.",
          variant: "destructive",
        });
      })
      .finally(() => setLoading(false));
  }, [id, toast]);

  const handleSubmit = async (values: BonusFormValues) => {
    if (!id) return;
    setLoading(true);
    try {
      const data = {
        ...values,
        startDate: format(values.startDate, "yyyy-MM-dd"),
        endDate: format(values.endDate, "yyyy-MM-dd"),
      };
      await axiosInstance.put(`/bonus/${id}`, data);
      toast({
        title: "Success",
        description: "Bonus program updated successfully",
        variant: "success",
      });
      navigate("/admin/bonuses");
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Unknown error occurred";
      setError("Failed to update bonus program");
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <BackLink to={`/admin/bonuses/${id}`}>Back to Bonus Details</BackLink>
      </div>

      <h1 className="text-3xl font-bold mb-8">Edit Bonus Program</h1>

      <div className="max-w-2xl mx-auto">
        {loading || !initialValues ? (
          <div className="h-40 flex items-center justify-center">
            Loading...
          </div>
        ) : (
          <BonusForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            loading={loading}
            submitLabel="Save Changes"
            cancelLabel="Cancel"
            onCancel={() => navigate(`/admin/bonuses/${id}`)}
          />
        )}
      </div>
    </div>
  );
}
